import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, MessageSquare, FolderOpen, CircleDot, Send, ChevronRight, CheckCircle2, Pencil, Trash2, RefreshCw, Pause, Play, Calendar, Ban } from 'lucide-react';
import { useProgressNotes, addProgressNote, deleteProgressNote } from '../../hooks/useProgressNotes';
import { useSubtasks, updateTask, deleteTask, createTask } from '../../hooks/useTasks';
import { useUpdateTemplate, usePauseTemplate, useResumeTemplate } from '../../hooks/useRecurrence';
import { useAppStore } from '../../store';
import { queryClient } from '../../api/queryClient';
import { api } from '../../api/client';
import type { Task } from '../../types';
import { format } from 'date-fns';

interface DetailPanelProps {
  task: Task;
  onClose: () => void;
}

export function DetailPanel({ task, onClose }: DetailPanelProps) {
  const [note, setNote] = useState('');
  const [drillTask, setDrillTask] = useState<Task | null>(null);
  const spaceId = useAppStore((s) => s.currentSpaceId);

  const notes = useProgressNotes(task.id);
  const allSubtasks = useSubtasks(task.id);

  const activeSubtasks = allSubtasks.filter((s) => s.status === 'track' || s.status === 'backlog');
  const completedSubtasks = allSubtasks.filter((s) => s.status === 'done');

  const isProject = task.itemType === 'project';
  const isSubtask = !!task.parentId;

  const computedProgress = (() => {
    if (isSubtask) return task.trackProgress;
    if (allSubtasks.length === 0) return 0;
    const total = allSubtasks.reduce((sum, st) => sum + (st.status === 'done' ? 100 : st.trackProgress), 0);
    return Math.round(total / allSubtasks.length);
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim()) return;
    await addProgressNote(task.id, note.trim(), task.trackProgress, task.trackProgress);
    setNote('');
    queryClient.invalidateQueries({ queryKey: ['progress-updates'] });
  };

  return (
    <>
      <motion.div
        initial={{ x: 384 }}
        animate={{ x: 0 }}
        exit={{ x: 384 }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="fixed top-0 right-0 h-full w-[min(24rem,100vw)] bg-bg-app border-l border-border-subtle shadow-2xl shadow-black/50 z-50 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
          <div className="flex items-center gap-2.5 min-w-0">
            {isProject ? (
              <FolderOpen size={16} className="shrink-0" style={{ color: task.color ?? '#6366f1' }} />
            ) : isSubtask ? (
              <span className="w-4 shrink-0 flex justify-center">
                <span className={`w-2.5 h-2.5 rounded-full ${
                  task.priority === 'p1' ? 'bg-p1' : task.priority === 'p2' ? 'bg-p2' : 'bg-p3'
                }`} />
              </span>
            ) : (
              <CircleDot size={16} className="text-accent shrink-0" />
            )}
            <EditableTitle taskId={task.id} title={task.title} spaceId={spaceId} status={task.status} />
          </div>
          <button onClick={onClose} aria-label="Close panel" className="text-text-muted hover:text-text-secondary p-2 rounded-lg interactive-ghost shrink-0">
            <X size={16} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Metadata card */}
          <div className="mx-3 mt-3 surface-card p-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-label text-text-muted block mb-1">Type</span>
                <span className="text-caption text-text-primary capitalize">{isSubtask ? 'Subtask' : task.itemType}</span>
              </div>
              <div>
                <span className="text-label text-text-muted block mb-1">Status</span>
                <span className="text-caption text-text-primary capitalize">{task.status}</span>
              </div>
              {isSubtask && (
                <div>
                  <span className="text-label text-text-muted block mb-1">Priority</span>
                  <div className="flex gap-1.5">
                    {(['p1', 'p2', 'p3'] as const).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={async () => { await updateTask(task.id, { priority: p }); }}
                        className={`px-2 py-0.5 rounded-md text-micro font-semibold transition-all
                          ${p === 'p1' ? 'bg-p1/15 text-p1' : p === 'p2' ? 'bg-p2/15 text-p2' : 'bg-p3/15 text-p3'}
                          ${task.priority === p ? 'ring-2 ring-offset-1 ring-offset-bg-surface' : 'opacity-40 hover:opacity-70'}
                          ${task.priority === p && p === 'p1' ? 'ring-p1' : task.priority === p && p === 'p2' ? 'ring-p2' : task.priority === p ? 'ring-p3' : ''}`}
                      >
                        {p.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <span className="text-label text-text-muted block mb-1">Created</span>
                <span className="text-caption text-text-secondary">{format(new Date(task.createdAt), 'MMM d')}</span>
              </div>
              <div className="col-span-2">
                <span className="text-label text-text-muted block mb-1.5">Progress</span>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 bg-border-subtle rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${computedProgress}%` }} />
                  </div>
                  <span className="text-micro text-text-secondary font-mono tabular-nums">{computedProgress}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="mx-3 mt-3">
            <DescriptionEditor taskId={task.id} description={task.description} />
          </div>

          {/* Recurrence */}
          <RecurrenceSection task={task} />

          {/* Subtasks */}
          {!isSubtask && allSubtasks.length > 0 && (
            <div className="mx-3 mt-3 surface-card p-3">
              <span className="text-label text-text-muted block mb-3">
                Subtasks ({allSubtasks.length})
              </span>

              {activeSubtasks.length > 0 && (
                <div className="space-y-1.5">
                  <span className="text-micro text-text-muted">Active</span>
                  {activeSubtasks.map((st) => (
                    <button key={st.id} onClick={() => setDrillTask(st)}
                      className="w-full surface-card flex items-start gap-2 px-3 py-2.5 hover:border-border-default transition-colors group text-left">
                      <span className="w-4 shrink-0 pt-1.5 flex justify-center">
                        <span className={`w-2 h-2 rounded-full ${st.priority === 'p1' ? 'bg-p1' : st.priority === 'p2' ? 'bg-p2' : 'bg-p3'}`} />
                      </span>
                      <div className="flex min-w-0 flex-1 items-start gap-2">
                        <span className="text-caption text-text-primary min-w-0 break-words flex-1">{st.title}</span>
                        <div className="flex shrink-0 items-center gap-1.5">
                          {!!st.blocked && <span className="text-label text-p1 bg-p1/10 px-1.5 py-0.5 rounded">Blocked</span>}
                          <span className="text-micro text-text-muted">{st.trackProgress}%</span>
                        </div>
                      </div>
                      <ChevronRight size={12} className="mt-1 text-text-muted opacity-0 group-hover:opacity-100 shrink-0" />
                    </button>
                  ))}
                </div>
              )}

              {completedSubtasks.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  <span className="text-micro text-finish">Completed ({completedSubtasks.length})</span>
                  {completedSubtasks.map((st) => (
                    <button key={st.id} onClick={() => setDrillTask(st)}
                      className="w-full surface-card flex items-center justify-between px-3 py-2.5 hover:border-border-default transition-colors group opacity-60">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 size={12} className="text-finish" />
                        <span className="text-caption text-text-secondary line-through">{st.title}</span>
                      </div>
                      <ChevronRight size={12} className="text-text-muted opacity-0 group-hover:opacity-100" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notes timeline */}
          <div className="mx-3 mt-3 mb-3 surface-card p-3">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare size={14} className="text-accent" />
              <span className="text-caption font-semibold text-text-primary">Activity</span>
              <span className="text-micro text-text-muted ml-auto">{notes.length}</span>
            </div>
            {notes.length === 0 ? (
              <p className="text-caption text-text-muted text-center py-4">No notes yet</p>
            ) : (
              <div className="relative pl-6">
                <div className="absolute left-2 top-2 bottom-2 w-px bg-border-subtle" />
                {notes.map((n) => (
                  <div key={n.id} className="relative mb-4 last:mb-0 group/note">
                    <div className="absolute -left-[18px] top-1 w-2.5 h-2.5 rounded-full border-2 border-accent bg-bg-surface" />
                    <div className="surface-card p-3">
                      <div className="flex items-start gap-2">
                        <p className="text-body text-text-primary break-all flex-1 whitespace-pre-wrap">{n.note}</p>
                        {n.progressBefore === n.progressAfter && (
                          <button
                            onClick={async () => { await deleteProgressNote(n.id); queryClient.invalidateQueries({ queryKey: ['progress-updates'] }); }}
                            className="shrink-0 p-1 rounded text-text-muted hover:text-p1 opacity-0 group-hover/note:opacity-100 transition-opacity"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-micro text-text-muted">{format(new Date(n.createdAt), 'MMM d, h:mm a')}</span>
                        {n.progressBefore !== n.progressAfter && (
                          <span className="text-micro text-accent font-medium">{n.progressBefore}% → {n.progressAfter}%</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* Note input */}
        <form onSubmit={handleSubmit} className="px-3 py-3 border-t border-border-subtle">
          <div className="flex items-end gap-2">
            <textarea value={note} onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') onClose(); if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }}
              placeholder="Add a note..."
              rows={1}
              className="flex-1 bg-bg-raised border border-border-default rounded-xl px-3.5 py-2.5 text-body text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent resize-none field-sizing-content max-h-32" />
            <button type="submit" className="p-2.5 bg-accent text-bg-base rounded-xl hover:bg-accent-hover transition-colors shrink-0">
              <Send size={14} />
            </button>
          </div>
        </form>
      </motion.div>

      {drillTask && <SubtaskDrillPanel task={drillTask} onClose={() => setDrillTask(null)} />}
    </>
  );
}

function SubtaskDrillPanel({ task, onClose }: { task: Task; onClose: () => void }) {
  const [note, setNote] = useState('');
  const notes = useProgressNotes(task.id);
  const isDone = task.status === 'done';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim()) return;
    await addProgressNote(task.id, note.trim(), task.trackProgress, task.trackProgress);
    setNote('');
    queryClient.invalidateQueries({ queryKey: ['progress-updates'] });
  };

  return (
    <div className="fixed top-0 right-96 h-full w-80 bg-bg-base border-l border-border-subtle shadow-2xl shadow-black/50 z-40 flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <div className="flex items-center gap-2 min-w-0">
          {isDone ? <CheckCircle2 size={14} className="text-finish" /> :
            <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${task.priority === 'p1' ? 'bg-p1' : task.priority === 'p2' ? 'bg-p2' : 'bg-p3'}`} />}
          <EditableTitle taskId={task.id} title={task.title} />
        </div>
        <button onClick={onClose} aria-label="Close subtask panel" className="text-text-muted hover:text-text-secondary p-2 rounded-lg interactive-ghost"><X size={16} /></button>
      </div>

      <div className="mx-3 mt-3 surface-card p-3 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="text-label text-text-muted block mb-1">Priority</span>
            <span className={`text-caption font-bold uppercase ${task.priority === 'p1' ? 'text-p1' : task.priority === 'p2' ? 'text-p2' : 'text-p3'}`}>{task.priority}</span>
          </div>
          <div>
            <span className="text-label text-text-muted block mb-1">Status</span>
            <button
              onClick={async () => {
                await updateTask(task.id, { blocked: !task.blocked });
                queryClient.invalidateQueries({ queryKey: ['tasks'] });
              }}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-micro font-semibold transition-colors ${
                task.blocked ? 'bg-p1/10 text-p1' : 'bg-bg-raised text-text-muted hover:text-text-secondary'
              }`}
            >
              <Ban size={11} />
              {task.blocked ? 'Blocked' : 'Block'}
            </button>
          </div>
        </div>
        <div>
          <span className="text-label text-text-muted block mb-1.5">Progress</span>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-border-subtle rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-accent" style={{ width: `${isDone ? 100 : task.trackProgress}%` }} />
            </div>
            <span className="text-micro text-text-secondary font-mono">{isDone ? 100 : task.trackProgress}%</span>
          </div>
        </div>
        {task.completedAt && (
          <div>
            <span className="text-label text-text-muted block mb-1">Completed</span>
            <span className="text-caption text-finish">{format(new Date(task.completedAt), 'MMM d, h:mm a')}</span>
          </div>
        )}
      </div>

      <div className="mx-3 mt-3">
        <DescriptionEditor taskId={task.id} description={task.description} />
      </div>

      <div className="flex-1 overflow-y-auto mx-3 mt-3 mb-3 surface-card p-3">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare size={12} className="text-accent" />
          <span className="text-caption font-semibold text-text-primary">Notes ({notes.length})</span>
        </div>
        {notes.length === 0 ? (
          <p className="text-caption text-text-muted text-center py-4">No notes on this subtask.</p>
        ) : (
          <div className="relative pl-6">
            <div className="absolute left-2 top-2 bottom-2 w-px bg-border-subtle" />
            {notes.map((n) => (
              <div key={n.id} className="relative mb-3 last:mb-0 group/note">
                <div className="absolute -left-[18px] top-1 w-2.5 h-2.5 rounded-full border-2 border-accent bg-bg-surface" />
                <div className="surface-card p-3">
                  <div className="flex items-start gap-2">
                    <p className="text-body text-text-primary break-all flex-1 whitespace-pre-wrap">{n.note}</p>
                    {n.progressBefore === n.progressAfter && (
                      <button
                        onClick={async () => { await deleteProgressNote(n.id); queryClient.invalidateQueries({ queryKey: ['progress-updates'] }); }}
                        className="shrink-0 p-1 rounded text-text-muted hover:text-p1 opacity-0 group-hover/note:opacity-100 transition-opacity"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-micro text-text-muted">{format(new Date(n.createdAt), 'MMM d, h:mm a')}</span>
                    {n.progressBefore !== n.progressAfter && <span className="text-micro text-accent">{n.progressBefore}% → {n.progressAfter}%</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="px-3 py-3 border-t border-border-subtle">
        <div className="flex items-end gap-2">
          <textarea value={note} onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') onClose(); if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }}
            placeholder="Add a note..."
            rows={1}
            className="flex-1 bg-bg-raised border border-border-default rounded-xl px-3.5 py-2.5 text-body text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent resize-none field-sizing-content max-h-32" />
          <button type="submit" className="p-2.5 bg-accent text-bg-base rounded-xl hover:bg-accent-hover shrink-0"><Send size={14} /></button>
        </div>
      </form>
    </div>
  );
}

function DescriptionEditor({ taskId, description }: { taskId: string; description?: string }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(description ?? '');

  useEffect(() => { setValue(description ?? ''); setEditing(false); }, [taskId, description]);

  const handleSave = async () => {
    await updateTask(taskId, { description: value.trim() || undefined });
    setEditing(false);
    queryClient.invalidateQueries({ queryKey: ['task', taskId] });
  };

  if (editing) {
    return (
      <div className="surface-card p-4">
        <textarea autoFocus value={value} onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') { setValue(description ?? ''); setEditing(false); } if (e.key === 'Enter' && e.metaKey) handleSave(); }}
          placeholder="Add a description..."
          className="w-full bg-bg-raised border border-border-default rounded-lg px-3 py-2 text-body text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent resize-none min-h-16" rows={3} />
        <div className="flex items-center gap-2 mt-3">
          <button onClick={handleSave} className="px-3.5 py-1.5 text-micro font-semibold bg-accent text-bg-base rounded-lg hover:bg-accent-hover">Save</button>
          <button onClick={() => { setValue(description ?? ''); setEditing(false); }} className="text-micro text-text-muted hover:text-text-secondary px-2">Cancel</button>
          <span className="text-micro text-text-muted ml-auto">⌘+Enter</span>
        </div>
      </div>
    );
  }

  return (
    <div className="surface-card p-4">
      {description ? (
        <div className="group">
          <div className="flex items-start justify-between">
            <p className="text-caption text-text-secondary whitespace-pre-wrap break-words overflow-hidden">{description}</p>
            <button onClick={() => setEditing(true)} className="text-text-muted hover:text-accent opacity-0 group-hover:opacity-100 transition-opacity p-1 shrink-0">
              <Pencil size={12} />
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setEditing(true)} className="text-caption text-text-muted hover:text-accent transition-colors">+ Add description</button>
      )}
    </div>
  );
}

function RecurrenceSection({ task }: { task: Task }) {
  const spaceId = useAppStore((s) => s.currentSpaceId);
  const [editing, setEditing] = useState(false);
  const [interval, setInterval] = useState(task.recurrenceInterval ?? 1);
  const [unit, setUnit] = useState<'days' | 'weeks' | 'months'>(task.recurrenceUnit ?? 'days');
  const [scheduledTime, setScheduledTime] = useState(task.defaultScheduledTime ?? '');
  const [endDate, setEndDate] = useState(task.recurrenceEndDate ?? '');
  const [startDate, setStartDate] = useState(task.recurrenceStartDate ?? '');
  const [newSubTitle, setNewSubTitle] = useState('');

  const pauseMutation = usePauseTemplate();
  const resumeMutation = useResumeTemplate();
  const updateMutation = useUpdateTemplate();

  const templateSubtasks = useSubtasks(task.id);

  const isTemplate = task.itemType === 'recurring';
  const isOccurrence = !!task.templateId;
  const isSubtask = !!task.parentId;

  if (isSubtask) return null;

  // Occurrence: show badge + link to template
  if (isOccurrence) {
    return (
      <div className="mx-3 mt-3 surface-card p-3">
        <div className="flex items-center gap-2">
          <RefreshCw size={14} className="text-accent" />
          <span className="text-caption text-text-primary">Recurring task</span>
          <button
            onClick={() => useAppStore.getState().setSelectedTaskId(task.templateId!)}
            className="text-caption text-accent hover:underline ml-auto"
          >
            Edit template
          </button>
        </div>
      </div>
    );
  }

  // Template: show editable rule
  if (isTemplate) {
    return (
      <div className="mx-3 mt-3 surface-card p-3">
        <div className="flex items-center gap-2 mb-3">
          <RefreshCw size={14} className="text-accent" />
          <span className="text-caption font-semibold text-text-primary">Recurrence</span>
          {task.recurrencePaused ? (
            <span className="text-micro text-p2 bg-p2/10 px-1.5 py-0.5 rounded ml-auto">Paused</span>
          ) : (
            <span className="text-micro text-finish bg-finish/10 px-1.5 py-0.5 rounded ml-auto">Active</span>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-caption text-text-muted">Every</span>
            <input
              type="number"
              min={1}
              value={interval}
              onChange={(e) => setInterval(Number(e.target.value))}
              className="w-14 bg-bg-raised border border-border-default rounded-lg px-2 py-1 text-caption text-text-primary focus:outline-none focus:border-accent"
            />
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value as 'days' | 'weeks' | 'months')}
              className="bg-bg-raised border border-border-default rounded-lg px-2 py-1 text-caption text-text-primary focus:outline-none focus:border-accent"
            >
              <option value="days">days</option>
              <option value="weeks">weeks</option>
              <option value="months">months</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <Calendar size={12} className="text-text-muted" />
            <span className="text-caption text-text-muted">Default time</span>
            <input
              type="time"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e.target.value)}
              className="bg-bg-raised border border-border-default rounded-lg px-2 py-1 text-caption text-text-primary focus:outline-none focus:border-accent"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-caption text-text-muted">Start date</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-bg-raised border border-border-default rounded-lg px-2 py-1 text-caption text-text-primary focus:outline-none focus:border-accent"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-caption text-text-muted">End date</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-bg-raised border border-border-default rounded-lg px-2 py-1 text-caption text-text-primary focus:outline-none focus:border-accent"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={async () => {
                await updateMutation.mutateAsync({
                  id: task.id,
                  changes: {
                    recurrenceInterval: interval,
                    recurrenceUnit: unit,
                    defaultScheduledTime: scheduledTime || null,
                    recurrenceStartDate: startDate || null,
                    recurrenceEndDate: endDate || null,
                  },
                });
              }}
              className="px-3 py-1.5 text-micro font-semibold bg-accent text-bg-base rounded-lg hover:bg-accent-hover"
            >
              Save
            </button>
            {task.recurrencePaused ? (
              <button
                onClick={() => resumeMutation.mutate(task.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-micro font-semibold text-finish border border-finish/30 rounded-lg hover:bg-finish/10"
              >
                <Play size={11} /> Resume
              </button>
            ) : (
              <button
                onClick={() => pauseMutation.mutate(task.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-micro font-semibold text-p2 border border-p2/30 rounded-lg hover:bg-p2/10"
              >
                <Pause size={11} /> Pause
              </button>
            )}
          </div>

          {/* Template subtasks */}
          <div className="mt-4 pt-3 border-t border-border-subtle">
            <span className="text-label text-text-muted block mb-2">
              Template subtasks ({templateSubtasks.length})
            </span>
            <p className="text-micro text-text-muted mb-2">These get cloned into every occurrence</p>
            {templateSubtasks.map((st) => (
              <div key={st.id} className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-bg-raised group">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full shrink-0 ${st.priority === 'p1' ? 'bg-p1' : st.priority === 'p2' ? 'bg-p2' : 'bg-p3'}`} />
                  <span className="text-caption text-text-primary">{st.title}</span>
                </div>
                <button
                  onClick={async () => { await deleteTask(st.id, { parentId: task.id }); }}
                  className="p-1 rounded text-text-muted hover:text-p1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!newSubTitle.trim()) return;
                await createTask(newSubTitle.trim(), task.spaceId, 'p2', 'template', { parentId: task.id });
                setNewSubTitle('');
              }}
              className="flex items-center gap-2 mt-2"
            >
              <input
                value={newSubTitle}
                onChange={(e) => setNewSubTitle(e.target.value)}
                placeholder="Add template subtask..."
                className="flex-1 bg-bg-raised border border-border-default rounded-lg px-2.5 py-1.5 text-caption text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
              />
              <button type="submit" className="px-2.5 py-1.5 text-micro font-semibold bg-accent/10 text-accent rounded-lg hover:bg-accent/20">
                Add
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Regular task: show "Make recurring" toggle
  return (
    <div className="mx-3 mt-3 surface-card p-3">
      <div className="flex items-center gap-2">
        <RefreshCw size={14} className="text-text-muted" />
        <span className="text-caption text-text-muted">Repeat</span>
        <button
          onClick={() => setEditing(!editing)}
          className={`ml-auto w-9 h-5 rounded-full transition-colors ${editing ? 'bg-accent' : 'bg-border-default'}`}
        >
          <div className={`w-3.5 h-3.5 rounded-full bg-white transition-transform ${editing ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
        </button>
      </div>

      {editing && (
        <div className="mt-3 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-caption text-text-muted">Every</span>
            <input
              type="number" min={1} value={interval}
              onChange={(e) => setInterval(Number(e.target.value))}
              className="w-14 bg-bg-raised border border-border-default rounded-lg px-2 py-1 text-caption text-text-primary focus:outline-none focus:border-accent"
            />
            <select value={unit} onChange={(e) => setUnit(e.target.value as any)}
              className="bg-bg-raised border border-border-default rounded-lg px-2 py-1 text-caption text-text-primary focus:outline-none focus:border-accent"
            >
              <option value="days">days</option>
              <option value="weeks">weeks</option>
              <option value="months">months</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <Calendar size={12} className="text-text-muted" />
            <span className="text-caption text-text-muted">Default time</span>
            <input type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)}
              className="bg-bg-raised border border-border-default rounded-lg px-2 py-1 text-caption text-text-primary focus:outline-none focus:border-accent" />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-caption text-text-muted">Start date</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="bg-bg-raised border border-border-default rounded-lg px-2 py-1 text-caption text-text-primary focus:outline-none focus:border-accent" />
          </div>

          <button
            onClick={async () => {
              await updateTask(task.id, {
                itemType: 'recurring',
                status: 'template',
                recurrenceInterval: interval,
                recurrenceUnit: unit,
                defaultScheduledTime: scheduledTime || undefined,
                recurrenceStartDate: startDate || undefined,
                recurrenceEndDate: endDate || undefined,
              });
              // Convert existing subtasks to template subtasks
              for (const st of templateSubtasks) {
                if (st.status !== 'template') {
                  await updateTask(st.id, { status: 'template' });
                }
              }
              queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] });
              queryClient.invalidateQueries({ queryKey: ['recurring-templates'] });
              await api.recurring.generate();
              queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] });
            }}
            className="px-3.5 py-1.5 text-micro font-semibold bg-accent text-bg-base rounded-lg hover:bg-accent-hover"
          >
            Make Recurring
          </button>
        </div>
      )}
    </div>
  );
}

function EditableTitle({ taskId, title, spaceId, status }: { taskId: string; title: string; spaceId?: string; status?: string }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setValue(title); }, [title]);
  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  const handleSave = async () => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== title) {
      await updateTask(taskId, { title: trimmed });
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      if (spaceId && status) {
        queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, status] });
      }
    } else {
      setValue(title);
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <input ref={inputRef} value={value} onChange={(e) => setValue(e.target.value)} onBlur={handleSave}
        onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') { setValue(title); setEditing(false); } }}
        className="text-body font-semibold text-text-primary bg-bg-raised border border-accent rounded-lg px-2 py-0.5 focus:outline-none w-full min-w-0" />
    );
  }

  return (
    <button onClick={() => setEditing(true)} className="text-body font-semibold text-text-primary hover:text-accent transition-colors text-left break-words min-w-0" title="Click to edit">
      {title}
    </button>
  );
}
